import * as events from './event-bus.js';
import * as store from './store.js';
import { notificationInterfaceFn } from '../NotificationInterface.js';
import { DEFAULT_POLL_TIME, MINIMUM_POLL_TIME, NOTIFICATION_SERVICE_ERRORS } from './constants.js';

export const setupPlatformNotifications = () => {
	const skConfig = store.get('sk-config');
	if (!skConfig) {
		// Preserve previous behavior where missing sk-config throws
		throw new Error('sk-config not available');
	}
	const notificationCfg = skConfig.notificationConfig || {};
	const {
		usePlatformNotifications,
		pollingIntervalInSeconds,
		notificationBaseUrl, // base URL for notifications
		includeCredentials = false,
	} = notificationCfg;
	if (!usePlatformNotifications) {
		console.warn(NOTIFICATION_SERVICE_ERRORS.NOT_ENABLED);
		return;
	}
	if(!notificationBaseUrl) {
		console.error(NOTIFICATION_SERVICE_ERRORS.BASE_URL_NOT_SET);
		return;
	}
	notificationInterfaceFn(store, notificationBaseUrl, includeCredentials);
	initializeNotifications(pollingIntervalInSeconds).initialize();
};

const initializeNotifications = (pollTime) => {
	const {
		NotificationServiceInterface: { get, markAsRead },
	} = window;
	const {
		legacyFormattedData: {
			user: { profileId: userId },
			productId,
		},
	} = store.get('passport-data');

	/**
	 * Filters out read notifications from the new notifications list.
	 * @param {Array} existingNotifications - The existing notifications.
	 * @param {Array} newNotifications - The new notifications to filter.
	 * @returns {Array} - The filtered list of unread notifications.
	 */
	function filterUnreadNotifications(
		existingNotifications,
		newNotifications
	) {
		// Return if no new notifications
		if (!newNotifications || !newNotifications.length) {return;}

		// Create a Set of all existing notification IDs
		const existingIds = new Set(existingNotifications.map((n) => n.id));

		// Filter for new unread notifications
		const newUnreadNotifications = newNotifications.filter(
			(n) => n.unread && !existingIds.has(n.id)
		);
		// Only update store if there are new unread notifications
		if (newUnreadNotifications.length > 0) {
			const updatedNotifications = [
				...newUnreadNotifications,
				...existingNotifications,
			];
			store.set('platform-notifications', updatedNotifications);
		}
	}

	/**
	 * Fetches notifications for the user.
	 * @returns {Promise<void>}
	 */
	async function fetchNotifications() {
		try {
			const response = await get(userId, productId.toLowerCase());
			const sortedResponse = Array.isArray(response)
				? response.sort((a, b) => {
					// Show unread first, then by latest timestamp
					if (b.unread !== a.unread) {
						return (b.unread === true) - (a.unread === true);
					}
					// Sort by timestamp descending (latest first)
					return new Date(b.timestamp) - new Date(a.timestamp);
				})
				: response;

			const platformNotifications = store.get('platform-notifications') || [];

			if (platformNotifications.length === 0 || platformNotifications.error) {
				// First time loading notifications or recovering from error - set all
				store.set('platform-notifications', sortedResponse);
			} else {
				// Merge new notifications with existing ones
				filterUnreadNotifications(platformNotifications, sortedResponse);
			}

		} catch (error) {
			console.error(`${NOTIFICATION_SERVICE_ERRORS.FETCH_ERROR}:${error}`);
			// Set error state in store for notifications component to handle
			store.set('platform-notifications', { error: true });
		}
	}

	/**
	 * Handles notification actions like marking as read or removal.
	 * @param {Object} notification - The notification object.
	 * @param {Function} action - The action function to perform (markAsRead or dismiss).
	 * @param {string} eventName - The event name to emit after action.
	 * @returns {Promise<void>}
	 */
	async function handleNotificationAction(notification, action, eventName) {
		if (!notification || !notification.id) {return;}

		try {
			if (notification && notification.unread) {
				await action(notification.id);
			}

			const platformNotifications = store.get('platform-notifications') || [];

			let updatedNotifications;
			// Update local store to reflect the change
			if (eventName === 'notification.read') {
				updatedNotifications = platformNotifications.map(function(n) {
					return n.id === notification.id
						? Object.assign({}, n, { unread: false })
						: n;
				});
			} else if (eventName === 'notification.removed') {
				// For removal, filter out the notification
				updatedNotifications = platformNotifications.filter((n) => n.id !== notification.id);
			}

			store.set('platform-notifications', updatedNotifications);
			// Emit event after successful action
			events.emit(eventName, notification);
		} catch (error) {
			console.error(`Failed to ${eventName}:`, error);
		}
	}

	const notificationActions = {
		read: async (notification) => {
			return handleNotificationAction(
				notification,
				markAsRead,
				'notification.read'
			);
		},

		remove: async (notification) =>
			handleNotificationAction(
				notification,
				markAsRead,
				'notification.removed'
			),
	};

	function startPolling() {
		fetchNotifications();
		const interval = pollTime >= MINIMUM_POLL_TIME ? pollTime : DEFAULT_POLL_TIME; // Ensure minimum interval of 30 seconds
		setTimeout(startPolling, interval);
	}

	function initialize() {
		events.on('notification.delete', notificationActions.remove);
		events.on('notification.mark-as-read', notificationActions.read);
		startPolling();
	}

	return {
		initialize,
	};
};
